// gifDecode — a framework-agnostic GIF89a/87a decoder.
//
// A faithful TypeScript port of the inline decoder shipped in the device-served
// `web/sheet.js` / `web/mosaic.js` (itself a JS port of the firmware's gif.cpp,
// validated by an encode→decode round-trip against our own encoder). Pure logic:
// no DOM, no canvas, no WebCodecs — it turns GIF bytes into per-frame palette
// indices + the palette, so it works everywhere (Node test env included) and is
// reusable by both the contact Sheet (B3) and the Mosaic (B4).
//
// It carries the `web/mosaic.js` extension — capturing each frame's Graphic
// Control Extension delay — so the Mosaic can loop clips at real speed; the Sheet
// ignores the delays. `delaySeconds` defaults to 0.1s when a frame omits a GCE,
// matching `web/mosaic.js` ((pend||10)/100).

/** One decoded GIF frame: its geometry, its (shared) RGB palette, and its per-pixel palette indices. */
export interface GifFrame {
  /** Frame width in pixels (the image-descriptor width). */
  readonly width: number;
  /** Frame height in pixels. */
  readonly height: number;
  /**
   * The active RGB color table for this frame (global, or a local table when the
   * frame carries one): `[r,g,b, r,g,b, …]`. Index `k` maps to bytes `k*3..k*3+2`.
   */
  readonly palette: Uint8Array;
  /** One palette index per pixel, row-major, `width*height` entries. */
  readonly indices: Uint8Array;
  /** This frame's display delay in **seconds** (GCE delay / 100; 0.1s default). */
  readonly delaySeconds: number;
}

/**
 * Decode up to `maxFrames` frames from a GIF byte buffer.
 *
 * Mirrors the device-served decoder exactly (same byte walk, same LZW), so a clip
 * the firmware/`web/` produced decodes identically here. Throws on a non-GIF
 * signature (the `web/` decoder throws too); a truncated stream stops early and
 * returns the frames decoded so far.
 *
 * @param buffer the GIF bytes (an `ArrayBuffer` or any `ArrayBufferView`).
 * @param maxFrames cap on frames to decode (a Clip is ≤ CLIP_MAX_FRAMES = 50).
 */
export function decodeGif(
  buffer: ArrayBuffer | ArrayBufferView,
  maxFrames: number = Number.MAX_SAFE_INTEGER,
): GifFrame[] {
  const b =
    buffer instanceof ArrayBuffer
      ? new Uint8Array(buffer)
      : new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  let p = 0;
  let pendingDelayCs = 0; // last GCE delay (centiseconds) awaiting its frame
  const frames: GifFrame[] = [];

  const u8 = (): number => b[p++]!;
  const u16 = (): number => {
    const v = b[p]! | (b[p + 1]! << 8);
    p += 2;
    return v;
  };
  // Skip a sub-block chain (1 length byte + that many bytes, until a zero length).
  const skipSubBlocks = (): void => {
    let len: number;
    while ((len = u8()) !== 0) p += len;
  };

  // Header: "GIF" magic, then the logical screen descriptor + optional global CT.
  if (b[0] !== 0x47 || b[1] !== 0x49 || b[2] !== 0x46) {
    throw new Error("not a gif");
  }
  p = 6;
  u16(); // logical screen width (unused — we trust the image descriptor)
  u16(); // logical screen height
  const packed = u8();
  p += 2; // background color index + pixel aspect ratio
  let gct: Uint8Array | null = null;
  if (packed & 0x80) {
    const gctEntries = 1 << ((packed & 7) + 1);
    gct = b.subarray(p, p + gctEntries * 3);
    p += gctEntries * 3;
  }

  while (p < b.length) {
    const sep = u8();
    if (sep === 0x3b) break; // trailer
    if (sep === 0x21) {
      // extension: capture a Graphic Control Extension's delay; skip the rest.
      const label = u8();
      if (label === 0xf9) {
        u8(); // block size (=4)
        u8(); // packed (disposal/transparency — unused)
        pendingDelayCs = u16(); // delay, centiseconds
        u8(); // transparent color index (unused)
        u8(); // block terminator (0)
      } else {
        skipSubBlocks();
      }
      continue;
    }
    if (sep !== 0x2c) break; // not an image descriptor — stop

    u16(); // image left
    u16(); // image top
    const iw = u16();
    const ih = u16();
    const ipacked = u8();
    let palette = gct;
    if (ipacked & 0x80) {
      // local color table — overrides the global one for this frame
      const lctEntries = 1 << ((ipacked & 7) + 1);
      palette = b.subarray(p, p + lctEntries * 3);
      p += lctEntries * 3;
    }
    const indices = lzwDecode(iw * ih);
    frames.push({
      width: iw,
      height: ih,
      palette: palette ?? new Uint8Array(0),
      indices,
      delaySeconds: (pendingDelayCs || 10) / 100,
    });
    pendingDelayCs = 0;
    if (frames.length >= maxFrames) break;
  }

  return frames;

  // LZW image-data decoder — the exact inverse of the firmware/`web/` encoder.
  // `np` = the expected number of pixels (width*height). Reads variable-width codes
  // little-endian from the image data sub-blocks; rebuilds strings via a prefix table.
  function lzwDecode(np: number): Uint8Array {
    const minCode = u8();
    const clear = 1 << minCode;
    const end = clear + 1;
    const prefix = new Int32Array(4096);
    const suffix = new Uint8Array(4096);
    const stack = new Uint8Array(4096);

    let codeSize = minCode + 1;
    let nextCode = clear + 2;
    let prev = -1;
    let firstByte = 0;
    const out = new Uint8Array(np);
    let written = 0;

    // sub-block bit reader, pulled lazily from the source
    let blockStart = 0;
    let blockLen = 0;
    let blockPos = 0;
    let dataEnd = false;
    let acc = 0;
    let accBits = 0;

    const refill = (): boolean => {
      if (dataEnd) return false;
      const len = b[p++]!;
      if (len === 0) {
        dataEnd = true;
        return false;
      }
      blockStart = p;
      blockLen = len;
      blockPos = 0;
      p += len;
      return true;
    };
    const getByte = (): number => {
      if (blockPos >= blockLen) {
        if (!refill()) return -1;
      }
      return b[blockStart + blockPos++]!;
    };
    const readCode = (): number => {
      while (accBits < codeSize) {
        const by = getByte();
        if (by < 0) return -1;
        acc |= by << accBits;
        accBits += 8;
      }
      const c = acc & ((1 << codeSize) - 1);
      acc >>>= codeSize;
      accBits -= codeSize;
      return c;
    };

    for (;;) {
      const code = readCode();
      if (code < 0) break;
      if (code === clear) {
        codeSize = minCode + 1;
        nextCode = clear + 2;
        prev = -1;
        continue;
      }
      if (code === end) break;

      let sp = 0;
      let cur: number;
      if (code < nextCode) {
        cur = code;
      } else if (code === nextCode && prev !== -1) {
        stack[sp++] = firstByte; // KwKwK special case
        cur = prev;
      } else {
        break; // malformed
      }
      while (cur >= clear) {
        stack[sp++] = suffix[cur]!;
        cur = prefix[cur]!;
      }
      firstByte = cur;
      stack[sp++] = cur;
      while (sp > 0) {
        if (written < np) out[written] = stack[--sp]!;
        else --sp;
        written++;
      }
      if (prev !== -1 && nextCode < 4096) {
        prefix[nextCode] = prev;
        suffix[nextCode] = firstByte;
        nextCode++;
        if (nextCode === 1 << codeSize && codeSize < 12) codeSize++;
      }
      prev = code;
    }

    if (!dataEnd) skipSubBlocks(); // consume the data block terminator
    return out;
  }
}
